/* hs-eslint ignored failing-rules */
/* eslint-disable @typescript-eslint/no-floating-promises */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import formatName from 'I18n/utils/formatName';
import { useAchievementByTrackId, useCreateAchievement, useUpdateAchievement } from './achievementQueries';
import { useFullChirpTrack } from '../tracks';
import { useUpdateAwardPermission } from '../awards/awardsQueries';
import { useUserInfo } from '../accounts';
import { useTrackProgress } from '../userProgress';
import { ACHIEVEMENT_VISIBILITIES } from '../../constants/AchievementConstants';
import { slugify, usernameFromEmail } from '../../utils/utils';
export const useMaybeCreateOrUpdateAchievement = trackId => {
  const {
    achievement,
    achievementsLoading,
    achievementsError
  } = useAchievementByTrackId(trackId);
  const {
    trackProgress
  } = useTrackProgress(trackId);
  const award = trackProgress === null || trackProgress === void 0 ? void 0 : trackProgress.award;
  const {
    track,
    trackLoading
  } = useFullChirpTrack(trackId);
  const {
    userInfo
  } = useUserInfo();
  const achievementCreationData = useMemo(() => {
    var _userInfo$user$emails;
    if (!track || !award || !userInfo) {
      return undefined;
    }
    const {
      firstName,
      lastName
    } = userInfo.user;
    const email = (_userInfo$user$emails = userInfo.user.emails[0]) !== null && _userInfo$user$emails !== void 0 ? _userInfo$user$emails : '';
    const userName = firstName || lastName ? formatName({
      firstName,
      lastName
    }) : usernameFromEmail(email);
    return {
      firstName,
      lastName,
      email,
      urlSlug: `${slugify(userName)}/${slugify(track.name)}`,
      visibilityType: award.visibility === 'PUBLIC' ? ACHIEVEMENT_VISIBILITIES.PUBLIC : ACHIEVEMENT_VISIBILITIES.PRIVATE,
      language: track.language,
      trackId
    };
  }, [track, award, userInfo, trackId]);
  const [createAchievement, {
    createAchievementLoading
  }] = useCreateAchievement(achievementCreationData ? {
    trackId: Number(achievementCreationData.trackId),
    urlSlug: achievementCreationData.urlSlug
  } : undefined);
  const hasAttemptedCreate = useRef(false);
  const isAwardInvalid = !award || !track;
  const isAchievementsServerError = achievementsError && achievementsError.status !== 404;
  useEffect(() => {
    if (achievementCreationData && !achievementsLoading && !isAchievementsServerError && !createAchievementLoading && !trackLoading && !isAwardInvalid) {
      if (!achievement && !hasAttemptedCreate.current) {
        hasAttemptedCreate.current = true;
        createAchievement();
      }
    }
  }, [achievement, achievementCreationData, achievementsLoading, createAchievement, createAchievementLoading, isAwardInvalid, trackLoading, isAchievementsServerError]);
  return {
    track,
    trackLoading,
    isAwardInvalid,
    achievementsLoading,
    achievementsError
  };
};
export const useUpdatePrivacy = ({
  trackId,
  awardCode,
  shouldBePublic
}) => {
  const {
    achievement
  } = useAchievementByTrackId(trackId);
  const [updateAchievement, {
    updateAchievementGlobalLoading
  }] = useUpdateAchievement(achievement, shouldBePublic ? 'PUBLIC' : 'PRIVATE');
  const [updateAwardPermission, {
    updateAwardPermissionGlobalLoading
  }] = useUpdateAwardPermission(awardCode /* guarded by condition in updatePrivacy */, shouldBePublic, trackId);
  const updatePrivacy = useCallback(() => {
    if (!awardCode) {
      return;
    }
    updateAchievement();
    updateAwardPermission();
  }, [awardCode, updateAchievement, updateAwardPermission]);
  return {
    updatePrivacy,
    updatePrivacyLoading: updateAwardPermissionGlobalLoading || updateAchievementGlobalLoading
  };
};
export const useAchievementStatus = trackId => {
  const {
    achievement
  } = useAchievementByTrackId(trackId);
  const {
    trackProgress
  } = useTrackProgress(Number(trackId));
  const award = trackProgress === null || trackProgress === void 0 ? void 0 : trackProgress.award;
  const isPrivate = !!achievement && !!award && (achievement.visibility === 'PRIVATE' || award.visibility === 'PRIVATE');
  const {
    updatePrivacyLoading
  } = useUpdatePrivacy({
    trackId,
    awardCode: award && award.code || '',
    shouldBePublic: !isPrivate
  });
  const isDisabled = !award || !achievement || updatePrivacyLoading || isPrivate;
  return {
    isPrivate,
    isDisabled
  };
};